import React from "react";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { InfoIcon } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { formatCurrency } from "../../utils/formatters";

interface LoanOriginationWaiverToggleProps {
  isWaived: boolean;
  builderName?: string;
  loanAmount: number;
  onWaiverToggle: (waived: boolean) => void;
  onBuilderNameChange: (name: string) => void;
}

export const LoanOriginationWaiverToggle: React.FC<LoanOriginationWaiverToggleProps> = ({
  isWaived,
  builderName,
  loanAmount,
  onWaiverToggle,
  onBuilderNameChange,
}) => {
  const originationFeeAmount = loanAmount * 0.01;

  return (
    <Card className="border-primary/20 bg-primary/5">
      <CardContent className="pt-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Label htmlFor="waiver-toggle" className="text-sm font-medium">
              Builder Partnership Fee Waiver
            </Label>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <InfoIcon className="h-4 w-4 text-muted-foreground" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs text-sm">
                    Partner builders may waive the loan origination fee (typically 1% of loan amount) 
                    as an incentive. This can save you thousands at closing.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <Switch
            id="waiver-toggle"
            checked={isWaived}
            onCheckedChange={onWaiverToggle}
          />
        </div>

        {isWaived && (
          <div className="space-y-3">
            <div>
              <Label htmlFor="builder-name" className="text-sm text-muted-foreground">
                Builder/Partner Name (Optional)
              </Label>
              <Input
                id="builder-name"
                value={builderName || ""}
                onChange={(e) => onBuilderNameChange(e.target.value)}
                placeholder="Enter builder name"
                className="mt-1"
              />
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="bg-green-100 text-green-800 border-green-200">
                Savings: {formatCurrency(originationFeeAmount)}
              </Badge>
              <span className="text-sm text-muted-foreground">
                Loan origination fee waived
              </span>
            </div>
          </div>
        )}

        {!isWaived && (
          <div className="text-sm text-muted-foreground">
            Standard loan origination fee: {formatCurrency(originationFeeAmount)} (1% of loan amount)
          </div>
        )}
      </CardContent>
    </Card>
  );
};